// pod-info.service.ts
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { EnvironmentService } from "./environment.service";
import { CodeServerConfig } from "../models/codeServer";

export interface PodInfo {
  id: string;
  podName: string;
  namespace: string;
  ready: boolean;
  status: string;
  restartCount: number;
  age: string;
  cpu: number;
  memory: number;
  node: string;
  podIP: string;
  deploymentName: string;
  podOwnerName: string;
  podOwnerUid: string;
  cpuInCore: string;
  memoryInMB: string;
  userName: string;
}

@Injectable({
  providedIn: "root",
})
export class PodInfoService {
  private httpOptions = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
  };

  constructor(private http: HttpClient, private env: EnvironmentService) {}

  // Fetch all pods
  getAllPods(): Observable<PodInfo[]> {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}`;
    return this.http
      .get<PodInfo[]>(apiUrl)
      .pipe(catchError(this.handleError<PodInfo[]>("getAllPods", [])));
  }

  // Fetch pods by deploymentName
  getPodsByDeployment(deploymentName: string, userName: string = 'all'): Observable<PodInfo[]> {
    let allPodsForAllUsers = this.env.envVariables.allPodsForAllUsers || false;
    if(allPodsForAllUsers){
      userName = 'all';
    }
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}PodInfoes/Deployment/${deploymentName}/UserName/${userName}/podlist`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http
      .get<PodInfo[]>(apiUrl, { headers: headers })
      .pipe(catchError(this.handleError<PodInfo[]>("getPodsByNamespace", [])));
  }

  // Fetch a single pod by ID
  getPodById(id: string): Observable<PodInfo> {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}`;
    return this.http
      .get<PodInfo>(`${apiUrl}/${id}`)
      .pipe(catchError(this.handleError<PodInfo>(`getPodById id=${id}`)));
  }

  // Add a new pod
  addPod(pod: PodInfo): Observable<PodInfo> {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}`;
    return this.http
      .post<PodInfo>(apiUrl, pod, this.httpOptions)
      .pipe(catchError(this.handleError<PodInfo>("addPod")));
  }

  // Update a pod by ID
  updatePod(pod: PodInfo): Observable<any> {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}`;
    return this.http
      .put(`${apiUrl}/${pod.id}`, pod, this.httpOptions)
      .pipe(catchError(this.handleError<any>("updatePod")));
  }

  deletePod(podName: string, podNamespace: string, depName: string) {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}PodInfoes/Deployment/${depName}/Delete/pod/${podName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(apiUrl, { headers: headers });
  }

  createCodeServer(codeServerInfo: CodeServerConfig, depName: string) {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}PodInfoes/Deployment/${depName}/DeployCodeServer`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(apiUrl, codeServerInfo, { headers: headers });
  }

  // Error handling
  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      return of(result as T);
    };
  }

  getSpecificPodInfo(depName: string, podName: string) {
    let apiUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}PodInfoes/Deployment/${depName}/podName/${podName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(apiUrl, { headers: headers });
  }
}
